import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, ProtectedRoute } from "./hooks/useAuth";
import { useChristmasTheme } from "./hooks/useChristmasTheme";
import { useRepublicDayTheme } from "./hooks/useRepublicDayTheme";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import DisclaimerModal from "./components/DisclaimerModal";
import { Snowfall, ChristmasBanner, FloatingTree } from "./components/christmas";
import { TricolorBanner, TricolorDecorations, FlagAnimation } from "./components/republic-day";
import Home from "./pages/Home";
import AboutUs from "./pages/AboutUs";
import Pricing from "./pages/Pricing";
import FAQs from "./pages/FAQs";
import ResearchService from "./pages/ResearchService";
import OurPerformance from "./pages/OurPerformance";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForceChangePassword from "./pages/ForceChangePassword";
import DetailedComplaints from "./pages/DetailedComplaints";
import TermsPage from "./pages/TermsPage";
import Disclaimer from "./pages/Disclaimer";
import CorporateAnnouncement from "./pages/CorporateAnnouncement";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CancellationPolicy from "./pages/CancellationPolicy";
import GrievancePolicy from "./pages/GrievancePolicy";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import PerformanceReports from "./pages/dashboard/PerformanceReports";
import StockRecommendations from "./pages/dashboard/StockRecommendations";
import Portfolio from "./pages/dashboard/Portfolio";
import Downloads from "./pages/dashboard/Downloads";
import WriteReview from "./pages/dashboard/WriteReview";
import Notifications from "./pages/dashboard/Notifications";
import Subscription from "./pages/dashboard/Subscription";
import IndexSearch from "./pages/dashboard/IndexSearch";
import MyStocks from "./pages/dashboard/MyStocks";
import LiveAnnouncements from "./pages/dashboard/LiveAnnouncements";
import CorporateAnnouncementDashboard from "./pages/dashboard/CorporateAnnouncementDashboard";
import Profile from "./pages/dashboard/Profile";
import HelpGuide from "./pages/dashboard/HelpGuide";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailure from "./pages/PaymentFailure";
import PlanDetail from "./pages/PlanDetail";
import { Toaster } from "./components/ui/toaster";

// Scroll handling on route change:
// - If the URL has a hash (e.g. /#download-report), scroll to that section.
//   The target may not be mounted yet right after navigation, so retry briefly.
// - Otherwise scroll to the top of the page.
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts < 10) {
          attempts += 1;
          setTimeout(tryScroll, 100);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const { isChristmasMode } = useChristmasTheme();
  const { isRepublicDayMode } = useRepublicDayTheme();
  
  // Show Christmas theme only on public pages
  const showChristmasTheme = isChristmasMode && !isDashboard;
  // Show Republic Day theme only on public pages
  const showRepublicDayTheme = isRepublicDayMode && !isDashboard;

  return (
    <div className={`App ${showChristmasTheme ? 'christmas-theme' : ''} ${showRepublicDayTheme ? 'republic-day-theme' : ''}`}>
      <ScrollToTop />
      <DisclaimerModal />
      {/* Christmas decorations for public pages only */}
      {showChristmasTheme && (
        <>
          <ChristmasBanner />
          <Snowfall />
          <FloatingTree />
        </>
      )}
      {/* Republic Day decorations for public pages only */}
      {showRepublicDayTheme && (
        <>
          <TricolorBanner />
          <FlagAnimation />
          <TricolorDecorations />
        </>
      )}
      {!isDashboard && <Header />}
      <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/research-service" element={<ResearchService />} />
          <Route path="/our-performance" element={<OurPerformance />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ForceChangePassword />} />
          <Route path="/detailed-complaints" element={<DetailedComplaints />} />
          <Route path="/terms-page" element={<TermsPage />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/corporate-announcement" element={<CorporateAnnouncement />} />
          <Route path="/privacy-page" element={<PrivacyPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/grievance-policy" element={<GrievancePolicy />} />
          <Route path="/plans/:planId" element={<PlanDetail />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/failure" element={<PaymentFailure />} />
          
          {/* Dashboard Routes - Protected */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardHome />} />
            <Route path="reports" element={<PerformanceReports />} />
            <Route path="reports/:id" element={<PerformanceReports />} />
            <Route path="stocks" element={<StockRecommendations />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="downloads" element={<Downloads />} />
            <Route path="review" element={<WriteReview />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="index-search" element={<IndexSearch />} />
            <Route path="my-stocks" element={<MyStocks />} />
            <Route path="live-announcement" element={<LiveAnnouncements />} />
            <Route path="corporate-announcement" element={<CorporateAnnouncementDashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="help" element={<HelpGuide />} />
          </Route>

          {/* Safety net: any unmatched URL (e.g. a malformed /plans/ link) lands on Home
              instead of rendering a blank page between the header and footer. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {!isDashboard && <Footer />}
        {!isDashboard && <ChatWidget />}
        <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
